import { MeshRenderer, TransformPosition, Node, TAG_GROUND } from '@ikea-aoa/elements-3d';
import {
	ConnectionManager,
	GridBox,
	SceneObject,
	View3dElement,
	Product,
	ProductData,
} from '@ikea-aoa/component-view-3d';
import { CameraController } from './CameraController';
import { SceneLogic } from './SceneLogic';
import { ObjectManager } from './ObjectManager';
import { DraggedObjectData, SnappingGrid, SnappingPoint } from '../../util/SceneUtils/SnappingGrid';
import { SmartMirrorManager } from '../../util/SceneUtils/SmartMirrorManager';
import { UiController } from './UiController';
import {
	InvalidPositionMap,
	PopOverSceneItemData,
	RangeSettings,
	SceneHelperData,
	SceneItemCoverConnection,
	TranslationsKey,
	CoverItem,
	IModuleSwiperItem,
	DeviceType,
	SceneItemData,
} from 'sofa-reborn-react';
import { BoxDimensions, Color } from '@ikea-aoa/elements-3d/lib/interfaces';
import { IPointerPosition } from '@inter-ikea-kompis/component-drag-and-drop';
import { SceneDataUtils } from './SceneDataUtils';

const DRAG_OFFSET = 10;
const SNAPPING_ERROR_TIME = 1000;

type Props = {
	dragItemId: string;
	selectedItemId: string;
	showSelectAll?: boolean;
	selectedSceneItem?: PopOverSceneItemData;
	moduleItems: IModuleSwiperItem[];
	rangeSettings: RangeSettings;
	showDebug?: boolean;
	sceneHelper?: SceneHelperData;
	selectedArmrest?: Product | null;
	cover: CoverItem;
	swiperEventType?: 'click' | 'drag' | null;
	specificItems: Map<string, SceneObject>;
	backrestAsArmrest?: SceneItemData | null;

	selectSceneItem: (item: PopOverSceneItemData | null) => void;
	setShowSelectAll: (value: boolean) => void;
	changeSceneItemCover: (sceneItemCoverConnection: SceneItemCoverConnection | null) => void;
	overSceneItemId: (value: string | null) => void;
	pointerOverDropArea: (value: boolean) => void;
	dragSceneItemId: (value: string | null) => void;
	setDragItemId: (value: string | null) => void;
	setSwiperEventType: (value: { type: 'click' | 'drag' | null; productId: string | null }) => void;
	setSelectedItemId: (value: string | null) => void;
	setNoRotationIcon: (value: boolean) => void;
	setHasFirstInteractionHappened: (value: boolean) => void;
	setSceneHelper: (value: SceneHelperData | null) => void;
	removeSceneItemOutsideScene: (sceneObejct: SceneObject) => void;
	saveScreenshot: (screenshot: string) => void;
	setKeepModule: (keepModule: boolean) => void;
	rotateCamera: () => void;
	setDisableMoveBackrest: (value: boolean) => void;
	setDisableMoveArmrestAsBackrest: (value: boolean) => void;
	clearAdditionalCoverItems: () => void;
	setBackrestAsArmrest: (item: SceneItemData | null) => void;
	setSpecificItems: (items: Map<string, SceneObject>) => void;
	setBuildTipHasBeenShown: (value: boolean) => void;
	setHideBuildTip: (value: boolean) => void;
};

export class ScenePointer {
	private sceneItems: Map<string, SceneObject>;
	private connectionManager: ConnectionManager;
	private objectManager: ObjectManager;
	private snappingGrid: SnappingGrid;
	private smartMirrorManager: SmartMirrorManager;

	private isPointerDown = false;
	private pointerDownOnNodeId: string | null = null;
	private pointerDownOnDropArea = false;
	private pointerDownOnGround = false;
	private pointOverDropArea = false;
	private dragStarted = false;
	private startPosition: IPointerPosition | null = null;

	private draggedItemInfo: DraggedObjectData | null;
	private draggedItem: SceneObject | null;
	private isSmartMirror = false;

	private activeGhost: SceneObject | null;
	private sceneGhosts: Map<SnappingPoint, SceneObject>;

	private closestSnappingPoint: SnappingPoint;
	private connectionErrorTimeout: number;
	private connectionError: boolean;
	private selector: MeshRenderer | null;

	private debugGhosts = new Array<Node>();
	private keyDown = false;

	constructor(
		private view3d: View3dElement,
		private logic: SceneLogic,
		private uiController: UiController,
		private cameraController: CameraController,
		private sceneDataUtils: SceneDataUtils,
		private rangeSettings: RangeSettings,
		private deviceType: DeviceType,
		private products: ProductData,
		private props: Props
	) {
		this.sceneItems = this.logic.sceneItems;
		this.connectionManager = this.logic.connectionManager;
		this.objectManager = this.logic.objectManager;
		this.snappingGrid = this.logic.snappingGrid;
		this.smartMirrorManager = this.logic.smartMirrorManager;

		this.sceneGhosts = new Map<SnappingPoint, SceneObject>();
	}

	/**
	 * Update props.
	 *
	 * @param props Props.
	 */
	public updateProps(props: Props): void {
		this.props = props;
	}

	/**
	 * OnPointerDown Handler.
	 *
	 * @param e Custom Event.
	 */
	public onPointerDown(e: CustomEvent): void {
		this.pointerDownOnGround = false;
		this.pointerDownOnNodeId = null;
		this.isPointerDown = true;
		this.cameraController.clearCameraRotationLock();

		const { x, y }: IPointerPosition = e.detail.position;
		this.startPosition = e.detail.position;
		const isOverDropArea = e.detail.isOverDropArea;
		this.pointerDownOnDropArea = isOverDropArea;
		if (!isOverDropArea) {
			return;
		}

		this.uiController.hideAllPopOvers();

		if (x && y) {
			const hit = this.view3d.getPointerHit(x, y);
			if (hit) {
				const id = hit.hits.find((h) => this.sceneItems.has(h));
				if (id) {
					/*
					 * We hit a scene item, stop camera control and prepare to
					 * identify click OR drag.
					 */
					this.pointerDownOnNodeId = id;
					this.cameraController.disableControl();
					this.props.changeSceneItemCover(null);
					this.setSmartMirrorWithId(id);
					this.props.setKeepModule(false);
				} else {
					this.pointerDownOnGround = true;
				}
			}
		}
	}

	/**
	 * OnPointerMove Handler.
	 *
	 * @param e Custom Event.
	 */
	public onPointerMove = (e: CustomEvent): void => {
		const position: IPointerPosition = e.detail.position;
		const { x, y } = position;
		const isOverDropArea: boolean = e.detail.isOverDropArea;

		this.createSceneHelper(position);

		if (this.isDragging(position)) {
			if (this.props.showSelectAll) {
				this.props.setShowSelectAll(false);
			}
			this.dragStarted = true;
			this.cameraController.stopCameraRotationAnimation();
			this.props.setHideBuildTip(true);
			setTimeout(() => {
				this.props.setBuildTipHasBeenShown(true);
				this.props.setHideBuildTip(false);
			}, 3000);

			/* Get Dragged item */
			this.getDraggedItem();

			/* Reset currently selected item */
			if (this.props.selectedSceneItem) {
				this.props.selectSceneItem(null);
				this.uiController.unselect();
			}
		}

		/* Handle if item is over drop area */
		if (isOverDropArea && x && y) {
			this.setOverDropAreaProp(true);
			const target = this.draggedItem ? TAG_GROUND : undefined;
			if (x && y) {
				const hit = this.view3d.getPointerHit(x, y, target);

				if (this.draggedItem) {
					this.objectManager.unhideItem(this.draggedItem);
					if (hit) {
						const position = { x: hit.position.x, z: hit.position.z };
						this.mirrorDraggedItem(position);
						this.moveDraggedItem(position);
					}
				} else if (this.props.dragItemId && hit) {
					const position = { x: hit.position.x, z: hit.position.z };
					let articleId = this.props.dragItemId;

					/* Creates a new scene item and set it as dragged item */
					if (this.shouldBeSmartMirrored(articleId)) {
						articleId = this.smartMirrorManager.getMirrorArticleWithArticleId(articleId);
					}
					this.setSmartMirror(articleId);
					this.createDraggedItem(articleId, position, false);
				} else if (
					this.pointerDownOnGround &&
					this.isPointerDown &&
					this.startPosition &&
					this.startPosition.x
				) {
					const isRotated = this.startPosition
						? x > this.startPosition?.x
							? x - 40 > this.startPosition?.x
							: x + 40 < this.startPosition?.x
						: false;
					if (isRotated) {
						this.props.rotateCamera();
					}
				}
			}
		} else {
			this.setOverDropAreaProp(false);
			if (this.draggedItem) {
				/* Hides dragged item since it is not over drop area and is about to be deleted */
				this.objectManager.hideItem(this.draggedItem);
			}
		}
	};

	/**
	 * OnPointerUp Handler.
	 *
	 * @param e Custom Event.
	 */
	public onPointerUp = (e?: CustomEvent): void => {
		if (!this.isPointerDown) {
			return;
		}
		this.isPointerDown = false;
		this.isSmartMirror = false;

		/* Click or Key down */
		if (!this.dragStarted || this.keyDown) {
			this.handleClick(e);
			if (!this.pointerDownOnDropArea) {
				if (e) {
					const element: Element | null = document.elementFromPoint(
						e.detail.position.x,
						e.detail.position.y
					);

					if (element?.tagName !== 'KOMPIS-IMAGE' && element?.tagName !== 'KOMPIS-TEXT') {
						this.props.setSelectedItemId(null);
					}
				}
				return;
			}
			this.props.selectSceneItem(null);
			const id = this.pointerDownOnNodeId;
			if (id) {
				const sceneObject = this.sceneItems.get(id);
				if (sceneObject) {
					/* Select an item */
					this.props.setDisableMoveBackrest(
						!this.logic.objectManager.automaticallyAddManager.isMovable(sceneObject)
					);
					const disconnectable =
						this.uiController.getPopOverSceneItemData(sceneObject)?.disconnectable || false;
					const sceneItemsArr = Array.from(this.sceneItems.values()).filter(
						(item) => !item.articleId.includes('backrest')
					);
					this.props.setDisableMoveArmrestAsBackrest(
						sceneItemsArr.length > 1
							? !this.logic.backrestAsArmrest(sceneObject) || !disconnectable
							: true
					);
					if (this.props.backrestAsArmrest && this.props.specificItems.has(sceneObject.id)) {
						this.uiController.setPopOverSceneItemData(sceneObject);
						this.uiController.selectSpecificItems(this.props.specificItems);
					} else {
						this.uiController.selectItem(sceneObject);
					}
				} else {
					throw 'SceneContainer: onPointerUp(): sceneObject does not exist';
				}
			}

			this.cameraController.enableControl();
			// Do nothing else.
			return;
		}

		/* Dragged Item */
		if (e && this.draggedItem) {
			const isOverDropArea = e.detail.isOverDropArea;

			if (this.draggedItem && isOverDropArea) {
				this.handlePlaceDraggedItem();
			} else if (this.draggedItem && !isOverDropArea) {
				/* Destroy item if it is placed outside of drop area */
				this.props.removeSceneItemOutsideScene(this.draggedItem);
				this.destroyDraggedItem();
			}

			this.smartRotateSceneItems();
			this.sceneDataUtils.updateSceneData(isOverDropArea);
			this.saveScreenshot();
		} else {
			/* No rotation icon when rotating scene */
			this.props.setNoRotationIcon(true);
			/* Module dragged and dropped in swiper, remove it */
			this.props.setDragItemId(null);
		}
		this.resetScene();
		this.dragStarted = false;
	};

	/**
	 * OnPointerNode Handler.
	 *
	 * @param e Custom Event.
	 */
	public onPointerOverNode = (e: CustomEvent): void => {
		const id = e.detail.nodeIds[0];
		this.props.overSceneItemId(id);
	};

	/**
	 * OnPointerOverGround Handler.
	 *
	 */
	public onPointerOverGround = (): void => {
		this.props.overSceneItemId(null);
	};

	/**
	 * Destoy snapping grid and remove all ghosts.
	 *
	 */
	public clear(): void {
		this.clearSnappingGrid();
		this.removeGhosts();
		this.removeSceneHelper();
	}

	/* Private methods */

	private resetScene(): void {
		this.cameraController.focusCamera(true);
		this.removeSceneHelper();
		this.cameraController.enableControl();
		this.uiController.updateTotalMeasurements();
	}

	private handleClick(e?: CustomEvent) {
		if ((this.isOverSwiper(e) || this.keyDown) && this.props.selectedItemId) {
			// Jattebo - reset backrest back if it has been moved
			if (this.props.backrestAsArmrest) {
				const parentSceneObject = this.getSpecificParent(this.props.specificItems);
				this.props.setBackrestAsArmrest(null);
				this.logic.objectManager.automaticallyAddManager.changeSeatConnection(parentSceneObject);
				this.logic.objectManager.automaticallyAddManager.remove(true);
				this.logic.objectManager.automaticallyAddManager.create();
				this.props.setSpecificItems(new Map<string, SceneObject>());
			}
			const limitReached = this.moduleLimitReached(
				this.sceneDataUtils.getSceneItems(),
				this.products,
				this.deviceType,
				this.rangeSettings
			);
			if (!limitReached) {
				this.keyDown = false;
				let articleId = this.props.selectedItemId;
				this.props.setSwiperEventType({ type: 'click', productId: articleId });
				if (this.shouldBeSmartMirrored(articleId)) {
					articleId = this.smartMirrorManager.getMirrorArticleWithArticleId(articleId);
				}
				this.createDraggedItem(
					articleId,
					{
						x: 0,
						y: 0,
						z: 0,
					},
					true
				);
				this.handlePlaceDraggedItem();
				this.smartRotateSceneItems();
				this.sceneDataUtils.updateSceneData();
				this.uiController.unselect();
				this.props.selectSceneItem(null);
				this.props.clearAdditionalCoverItems();
				this.saveScreenshot();
				this.resetScene();
			}
		}
	}

	private moduleLimitReached = (
		sceneItems: SceneItemData[],
		products: ProductData,
		deviceType: DeviceType,
		rangeSettings: RangeSettings
	): boolean => {
		let nrOfSeats = 0;

		sceneItems.forEach((sceneObject) => {
			const sceneObjectFamily = products[sceneObject.articleId].filter.family;
			/* Armrests, headrests and legs are not considered as seat modules. */
			if (
				sceneObjectFamily !== 'ARMREST' &&
				sceneObjectFamily !== 'HEADREST' &&
				sceneObjectFamily !== 'BACKREST' &&
				sceneObjectFamily !== 'CUSHION' &&
				sceneObject.articleId !== 'leg'
			) {
				nrOfSeats++;
			}
		});

		return deviceType === 'mobile'
			? nrOfSeats >= rangeSettings.moduleLimitMobile
			: nrOfSeats >= rangeSettings.moduleLimit;
	};

	private isOverSwiper(e?: CustomEvent) {
		if (e) {
			const element = document.elementFromPoint(e.detail.position.x, e.detail.position.y);
			return this.checkParentElementForTag('kompis-drag-and-drop-item', element, 5);
		}
		return false;
	}

	private checkParentElementForTag(search: string, element?: Element | null, counter = 0): boolean {
		if (element?.tagName.toLowerCase() === search) {
			return true;
		}
		const parent = element?.parentElement;
		if (counter > 0 && parent) {
			return this.checkParentElementForTag(search, parent, --counter);
		}
		return false;
	}

	private handlePlaceDraggedItem(): void {
		/* Try to place an item on scene using a grid */
		const placed = this.placeDraggedItem();
		if (!placed) {
			if (this.logic.oneModuleOnScene()) {
				/* If there is only one module on scene we can free-place it */
				this.stopDragging();
				this.cameraController.startCameraRotationLock();
			} else {
				/* Destroy it. We should never be here if swiper works correctly */
				this.destroyDraggedItem();
			}
		}
	}

	private setDraggedItem(item: SceneObject | string): void {
		this.draggedItem = this.getObject(item);

		/* Disable mouse interactions with dragged item */
		this.objectManager.disableComponent(this.draggedItem, 'Interactor');

		/* Hide extended bounds */
		this.objectManager.disableComponent(this.draggedItem, 'BoundsRenderer');

		/* Remove models that can prevent connections */
		if (!this.connectionManager.isAutoConnectMovable(this.draggedItem)) {
			this.objectManager.automaticallyAddManager.remove(
				this.props.rangeSettings.rangeName !== 'JÄTTEBO',
				{
					draggedItem: this.draggedItem,
					isSmartMirror: this.isSmartMirror,
				},
				this.props.swiperEventType
			);
		}

		/* Create snapping grid with empty ghosts */
		this.createSnappingGridForObject(this.draggedItem);

		/* Update snapping information */
		this.updateDraggedItemSnappingInfo();

		/* Set active ghost */
		this.setActiveGhost(this.draggedItemInfo ? this.draggedItemInfo.closestValid : null);

		/* Create connection error timeout if needed */
		this.setConnectionErrorPopoverTimeout();

		/* Set dragged item prop */
		this.setDraggedItemProp(this.draggedItem);
	}

	private moveDraggedItem(position: TransformPosition): void {
		/* Update item position */
		this.objectManager.setPosition(this.draggedItem as SceneObject, position);

		/* Update snapping information */
		this.updateDraggedItemSnappingInfo();

		/* Set active ghost and set render component to active ghost*/
		this.setActiveGhost(this.draggedItemInfo ? this.draggedItemInfo.closestValid : null);

		/* Create connection error timeout if needed */
		this.setConnectionErrorPopoverTimeout();
	}

	private mirrorDraggedItem(position: TransformPosition): void {
		if (this.isSmartMirror && this.smartMirrorManager.shouldMirror(this.draggedItem)) {
			this.isSmartMirror = false;
			const mirrorArticle = this.smartMirrorManager.getMirrorArticle(this.draggedItem);
			if (mirrorArticle) {
				this.destroyDraggedItem();
				this.createDraggedItem(mirrorArticle, position, false);
				if (this.pointerDownOnNodeId && this.draggedItem) {
					this.pointerDownOnNodeId = this.draggedItem.id;
				}
			}
			this.isSmartMirror = true;
		}
	}

	private shouldBeSmartMirrored(articleId: string): boolean {
		const { smartMirrorModules, rangeName } = this.props.rangeSettings;
		return (
			this.smartMirrorManager.isSmartMirrorArticle(articleId, smartMirrorModules) &&
			!this.logic.getArticlePlaceableStatus({
				articleId,
				isSmartMirror: true,
				keepAutoConnect: rangeName === 'JÄTTEBO',
			})
		);
	}

	private setSmartMirrorWithId(sceneId: string): void {
		const obj = this.sceneItems.get(sceneId);
		if (obj) {
			this.setSmartMirror(obj.articleId, sceneId);
		}
	}

	private setSmartMirror(articleId: string, sceneId?: string): void {
		if (
			this.smartMirrorManager.isSmartMirrorArticle(
				articleId,
				this.props.rangeSettings.smartMirrorModules
			)
		) {
			const ignore = sceneId ? [sceneId] : undefined;
			const mirrorArticle = this.smartMirrorManager.getMirrorArticleWithArticleId(articleId);
			if (mirrorArticle) {
				const { rangeName } = this.props.rangeSettings;
				this.isSmartMirror = this.logic.getArticlePlaceableStatus({
					articleId: mirrorArticle,
					isSmartMirror: true,
					keepAutoConnect: rangeName === 'JÄTTEBO',
					ignore,
				});
			}
		}
	}

	private placeDraggedItem(): boolean {
		/* Clear snapping grid */
		this.clearSnappingGrid();

		/* Remove all ghosts and set renderComponent back to dragged item */
		this.removeGhosts();

		/* Connect or place item to closest valid snapping point position */
		if (this.draggedItemInfo && this.draggedItemInfo.closestValid) {
			const { connection, transform } = this.draggedItemInfo.closestValid;
			if (connection) {
				const { parentId, relation } = connection;
				const parent = this.sceneItems.get(parentId);
				this.logic.moduleConnectionManager.connect(
					this.draggedItem as SceneObject,
					parent as SceneObject,
					relation
				);
			} else {
				this.objectManager.setPosition(
					this.draggedItem as SceneObject,
					transform.position as TransformPosition
				);
			}
		} else {
			/* Return false if there are no valid snapping points */
			return false;
		}

		this.stopDragging();
		return true;
	}

	private stopDragging(): void {
		if (!this.draggedItem) {
			return;
		}

		/* Enable mouse interactions with item */
		this.logic.objectManager.enableComponent(this.draggedItem, 'Interactor');

		/* Show extended bounds */
		this.logic.objectManager.enableComponent(this.draggedItem, 'BoundsRenderer');

		/* Create auto-armrests */
		this.logic.objectManager.automaticallyAddManager.create();

		/* Create and update current legs */
		this.logic.objectManager.legsManager.updateLegs(
			!this.props.rangeSettings.coversWithNoLegAssembly?.includes(this.props.cover.coverName)
		);

		this.draggedItem = null;
		this.draggedItemInfo = null;

		/* Set dragged item prop */
		this.setDraggedItemProp(null);
	}

	private destroyDraggedItem(): void {
		if (!this.draggedItem) {
			return;
		}

		/* Clear snapping grid */
		this.clearSnappingGrid();

		/* Remove all ghosts and set renderComponent back to dragged item */
		this.removeGhosts();

		this.draggedItemInfo = null;
		if (!this.connectionManager.isAutoConnectMovable(this.draggedItem)) {
			const { id } = this.draggedItem;
			this.draggedItem = null;
			this.objectManager.destroyObject(id);
		} else {
			this.draggedItem = null;
			this.objectManager.automaticallyAddManager.remove(
				this.props.rangeSettings.rangeName !== 'JÄTTEBO'
			);
		}

		/* Create auto-models */
		this.objectManager.automaticallyAddManager.create();

		/* Create and update current legs */
		this.objectManager.legsManager.updateLegs(
			!this.props.rangeSettings.coversWithNoLegAssembly?.includes(this.props.cover.coverName)
		);

		/* Set dragged item prop */
		this.setDraggedItemProp(null);
	}

	private clearSnappingGrid(): void {
		this.snappingGrid.clear();
		this.selector = null;
	}

	private setOverDropAreaProp(value: boolean): void {
		if (this.pointOverDropArea !== value) {
			this.pointOverDropArea = value;
			this.props.pointerOverDropArea(value);
		}
	}

	private createDraggedItem(
		articleId: string,
		position: TransformPosition,
		addedWithClick: boolean
	): SceneObject {
		const item = this.objectManager.createObject(articleId, position, addedWithClick);
		if (item) {
			// Jattebo - reset backrest back if it has been moved
			if (this.props.backrestAsArmrest) {
				const parentSceneObject = this.getSpecificParent(this.props.specificItems);
				this.props.setBackrestAsArmrest(null);
				this.logic.objectManager.automaticallyAddManager.changeSeatConnection(parentSceneObject);
				this.logic.objectManager.automaticallyAddManager.remove(true);
				this.logic.objectManager.automaticallyAddManager.create();
				this.props.setSpecificItems(new Map<string, SceneObject>());
			}
			this.setDraggedItem(item);
			return item;
		} else {
			throw 'ScenePointer: createDraggedItem(): can not create item';
		}
	}

	private getDraggedItem(): void {
		if (this.pointerDownOnNodeId && !this.draggedItem) {
			const specificItemsArr = this.props.specificItems
				? Array.from(this.props.specificItems.keys())
				: [];
			let targetObject =
				specificItemsArr.length > 0 && specificItemsArr.includes(this.pointerDownOnNodeId)
					? this.getSpecificParent(this.props.specificItems)
					: this.sceneItems.get(this.pointerDownOnNodeId);
			if (targetObject) {
				if (this.props.backrestAsArmrest) {
					const parentSceneObject = this.getSpecificParent(this.props.specificItems);
					this.props.setBackrestAsArmrest(null);
					this.logic.objectManager.automaticallyAddManager.changeSeatConnection(parentSceneObject);
					this.logic.objectManager.automaticallyAddManager.remove(true);
					this.logic.objectManager.automaticallyAddManager.create();
					this.props.setSpecificItems(new Map<string, SceneObject>());
					this.pointerDownOnNodeId = parentSceneObject.id;
				} else {
					if (
						targetObject.parentId &&
						this.connectionManager.isAutoConnectableModel(targetObject.articleId) &&
						!this.connectionManager.isAutoConnectMovable(targetObject)
					) {
						targetObject = this.sceneItems.get(targetObject.parentId) as SceneObject;
						this.pointerDownOnNodeId = targetObject.id;
					}
					const disconnect = this.tryToDisconnect(targetObject);
					if (disconnect) {
						this.setDraggedItem(targetObject);
					}
				}
			} else {
				throw 'PointerManager: getDraggedItem(): targetObject does not exist';
			}
		}
	}

	private isDragging(position: IPointerPosition): boolean {
		const start = this.startPosition;
		if (start && position && start.x && start.y && position.x && position.y) {
			const moveOffset = this.startPosition
				? Math.hypot(position.x - start.x, position.y - start.y)
				: null;
			if (this.isPointerDown && moveOffset && moveOffset >= DRAG_OFFSET) {
				return true;
			}
		}
		return false;
	}

	private setDraggedItemProp(value: SceneObject | null): void {
		const itemId = value ? value.articleId : null;
		const id = value ? value.id : null;
		this.props.setDragItemId(itemId);
		if (itemId !== null) {
			this.props.setHasFirstInteractionHappened(true);
		}
		this.props.dragSceneItemId(id);
	}

	private createSceneHelper(position: IPointerPosition): void {
		if (this.pointerDownOnNodeId && this.draggedItem) {
			const targetObject = this.sceneItems.get(this.pointerDownOnNodeId);
			if (targetObject) {
				if (!this.props.moduleItems.find((item) => targetObject.articleId === item.id)) {
					const { x, y } = position;
					if (x && y) {
						this.props.setSceneHelper({ id: targetObject.articleId, pos: { x, y } });
					}
				}
			} else {
				throw 'PointerManager: createSceneHelper(): targetObject does not exist';
			}
		}
	}

	private removeSceneHelper(): void {
		if (this.props.sceneHelper) {
			this.props.setSceneHelper(null);
		}
	}

	private smartRotateSceneItems(): void {
		if (this.props.rangeSettings.smartRotateModules) {
			this.logic.smartRotateSceneItems();
		}
	}

	private setActiveGhost(snappingPoint: SnappingPoint | null): void {
		if (!snappingPoint || !this.draggedItem) {
			return;
		}
		const closestGhost = this.sceneGhosts.get(snappingPoint);
		const activeNode = this.activeGhost ? this.activeGhost.node : this.draggedItem.node;
		if (activeNode) {
			const renderComponent = activeNode.getComponent('MeshRenderer') as MeshRenderer;
			if (closestGhost && closestGhost.node && closestGhost.node !== activeNode) {
				activeNode.detachComponent(renderComponent);
				closestGhost.node.attachComponent(renderComponent);
				// change child nodes parent
				if (activeNode.children.size) {
					activeNode.children.forEach((n) => n.setParent(closestGhost.node as Node));
				}
				// set ghost overlay
				this.objectManager.setOverlay(closestGhost.node as Node, true);

				// hide legs ??? do we need it here? TEST
				this.objectManager.legsManager.hideObjects(closestGhost.node as Node);
			}
			/* Focus camera on Ghost and Scene Items */
			this.focusCameraOnActive();
			this.activeGhost = closestGhost ? closestGhost : null;
		}
	}

	private tryToDisconnect(sceneObject: SceneObject): boolean {
		const isAutoConnectMovable = this.connectionManager.isAutoConnectMovable(sceneObject);
		if (
			(this.connectionManager.isAutoConnectableModel(sceneObject.articleId) &&
				!isAutoConnectMovable) ||
			(isAutoConnectMovable && !this.objectManager.automaticallyAddManager.isMovable(sceneObject))
		) {
			return false;
		}

		if (!this.logic.isDisconnectable(sceneObject)) {
			// Can not disconnect middle section items - show popup
			this.uiController.showPopOver('middle-module-drag', sceneObject);
			return false;
		}

		const childSections = this.connectionManager.getChildStandaloneObjects(sceneObject);
		if (this.logic.isModularSofa()) {
			childSections.forEach((childSection) => {
				this.logic.moduleConnectionManager.disconnect(childSection);
			});
		} else if (childSections.length == 1 && !sceneObject.parentId) {
			this.logic.moduleConnectionManager.disconnect(childSections[0]);
			return true;
		}

		this.logic.moduleConnectionManager.disconnect(sceneObject);
		return true;
	}

	private removeGhosts(): void {
		// Put back renderComponent if it was detached and setting children nodes back
		if (this.activeGhost && this.activeGhost.node && this.draggedItem && this.draggedItem.node) {
			const renderComponent = this.activeGhost.node.getComponent<MeshRenderer>('MeshRenderer');
			renderComponent.state.enableOverlay = typeof renderComponent.state.mesh === 'object';
			this.activeGhost.node.detachComponent(renderComponent);
			this.draggedItem.node.attachComponent(renderComponent);
			// reconnect child nodes
			if (this.activeGhost.node.children.size) {
				this.activeGhost.node.children.forEach((n) => {
					const draggedItem = this.draggedItem;
					if (draggedItem && draggedItem.node) {
						n.setParent(draggedItem.node);
					}
				});
			}
			// remove overlay from dragged item TODO do we need to?
			this.objectManager.setOverlay(this.draggedItem.node, false);

			this.activeGhost = null;
		}
		// Destroy snapping ghosts.
		this.sceneGhosts.forEach((g) => this.objectManager.destroyObject(g));
		this.sceneGhosts.clear();

		// unhide all children. TODO do we need to?
		if (this.draggedItem && this.draggedItem.node) {
			this.draggedItem.node.getAllChildNodes().forEach((n) => (n.visibility = 1));
		}

		// remove popovers
		this.uiController.hidePopOver('invalid-placement');

		// DEBUG
		this.debugGhosts.forEach((g) => this.objectManager.destroyNode(g));
		this.debugGhosts = new Array<Node>();
	}

	private focusCameraOnActive(): void {
		if (!this.draggedItemInfo || !this.draggedItem) {
			return;
		}
		const closestValidPoint = this.draggedItemInfo.closestValid;
		if (closestValidPoint) {
			const closestGhost = this.sceneGhosts.get(closestValidPoint);

			if (closestGhost && this.activeGhost !== closestGhost) {
				const focusItems = new Map<string, SceneObject>(this.sceneItems);
				focusItems.delete(this.draggedItem.id);
				focusItems.set(closestGhost.id, closestGhost);
				const children = this.connectionManager.getChildren(this.draggedItem.id);
				children.forEach((c) => focusItems.delete(c));
				if (this.logic.isModularSofa()) {
					// Focus on scene items and possible scene placements
					this.sceneGhosts.forEach((ghost, point) => {
						if (point.connection && point.isValid && !point.intersections.length) {
							focusItems.set(ghost.id, ghost);
						}
					});
				}
				this.cameraController.focusCamera(false, focusItems);
			}
		}
	}

	private setConnectionErrorPopoverTimeout(): void {
		if (!this.draggedItemInfo) {
			return;
		}
		const { closest, closestConnection, intersections } = this.draggedItemInfo;

		let closestPoint = closest;

		if (intersections.length && closestConnection && closestConnection.connection) {
			if (intersections.includes(closestConnection.connection.parentId)) {
				closestPoint = closestConnection;
			}
		}
		// remove popover
		if (this.closestSnappingPoint !== closestPoint && this.connectionError) {
			this.uiController.hidePopOver('invalid-placement');
			this.connectionError = false;
		}

		if (!closestPoint) {
			return;
		}

		// new popover timeout
		if (!closestPoint.isValid || closestPoint.intersections.length) {
			if (this.closestSnappingPoint != closestPoint) {
				this.setErrorConnectionTimeout(closestPoint);
			}
		} else {
			clearTimeout(this.connectionErrorTimeout);
		}

		// SELECTOR
		const sceneGhost = this.sceneGhosts.get(closestPoint);
		if (sceneGhost) {
			const node = sceneGhost.node;
			if (!this.selector && node) {
				this.selector = node.addComponent(MeshRenderer, 'Selector');
				this.selector.state.mesh = { width: 0.1, depth: 0.1, height: 0.1 };
				this.selector.state.visibility = 0;
			} else if (this.closestSnappingPoint !== closestPoint) {
				const old = this.sceneGhosts.get(this.closestSnappingPoint);
				if (this.selector && old && old.node && node) {
					old.node.detachComponent(this.selector);
					node.attachComponent(this.selector);
				}
			}
		} else {
			throw 'PointerManager: setConnectionErrorPopoverTimeout(): sceneGhost does not exist';
		}

		this.updateClosestSnappingPoint(closestPoint);
	}

	private updateClosestSnappingPoint(closestPoint: SnappingPoint): void {
		// DEBUG
		if (this.props.showDebug) {
			// reset all snapping points
			this.snappingGrid.getGridSnappingPoints().forEach((point) => {
				const ghost = this.sceneGhosts.get(point);
				if (ghost && ghost.node) {
					const node = ghost.node;
					const state = node.getComponent<MeshRenderer>('Preview').state;
					if (state.transform) {
						state.visibility = 0.5;
						state.transform.scale = { x: 1, y: 1, z: 1 };
					}
				} else {
					throw 'SceneContainer: setClosestSnappingPoint(): ghost does not exist';
				}
			});

			this.snappingGrid.getConnectionSnappingPoints().forEach((point) => {
				const ghost = this.sceneGhosts.get(point);
				if (ghost && ghost.node) {
					const node = ghost.node;
					const state = node.getComponent<MeshRenderer>('Preview').state;
					if (state.transform) {
						state.visibility = 0.5;
						state.transform.scale = { x: 1, y: 1, z: 1 };
					}
				} else {
					throw 'SceneContainer: setClosestSnappingPoint(): ghost does not exist';
				}
			});

			// highlight closest
			const ghost = this.sceneGhosts.get(closestPoint);
			if (ghost && ghost.node) {
				const node = ghost.node;
				const state = node.getComponent<MeshRenderer>('Preview').state;
				if (state.transform) {
					state.visibility = 0.85;
					state.transform.scale = { x: 1.5, y: 1.5, z: 1.5 };
				}
			} else {
				throw 'SceneContainer: setClosestSnappingPoint(): ghost does not exist';
			}

			// show gridboxes
			if (this.closestSnappingPoint !== closestPoint) {
				// delete current ghosts
				this.debugGhosts.forEach((g) => this.objectManager.destroyNode(g));
				this.debugGhosts = new Array<Node>();

				// render new
				closestPoint.gridBoxes.forEach((box) => this.renderDebugGridBox(box));

				// render intersections
				closestPoint.intersections.forEach((int) =>
					this.renderDebugGridBox(int.gridBox, { r: 1, b: 0, g: 0 })
				);
			}
		}
		this.closestSnappingPoint = closestPoint;
	}

	private setErrorConnectionTimeout(snappingPoint: SnappingPoint): void {
		clearTimeout(this.connectionErrorTimeout);
		const { articleId, connection } = snappingPoint;
		if (!connection) {
			return;
		}
		const sceneItem = this.sceneItems.get(connection.parentId);
		if (sceneItem) {
			const parentArticleId = sceneItem.articleId;
			const type = this.getInvalidConnectionType(articleId, parentArticleId);
			if (!type) {
				return;
			}
			this.connectionErrorTimeout = window.setTimeout(() => {
				if (this.selector) {
					const screenPosition = this.selector.getScreenPosition(true);
					this.uiController.showPopOver(
						'invalid-placement',
						screenPosition,
						type as TranslationsKey
					);
					this.connectionError = true;
				}
			}, SNAPPING_ERROR_TIME);
		} else {
			throw 'PointerManager: setErrorConnectionTimeout(): sceneItem does not exist';
		}
	}

	private getInvalidConnectionType(childArticleId, parentArticleId): string | null {
		const map = this.props.rangeSettings.invalidPositionCopyMap as InvalidPositionMap;
		const childFamily = this.connectionManager.getFamily(childArticleId);
		if (!childFamily || !map) {
			return null;
		}
		const parentFamily = this.connectionManager.getFamily(parentArticleId);
		const childInfo = map[childFamily];
		if (!childInfo || !parentFamily) {
			return null;
		}
		const type = childInfo[parentFamily];
		return type ? type : null;
	}

	private updateDraggedItemSnappingInfo(): void {
		if (this.draggedItem) {
			const parentId = this.draggedItemInfo?.closestValid?.connection?.parentId;
			if (
				parentId &&
				this.connectionManager.isAutoConnectMovable({ ...this.draggedItem, parentId })
			) {
				this.draggedItemInfo = this.snappingGrid.getDraggedObjectData(this.draggedItem, parentId);
			} else {
				this.draggedItemInfo = this.snappingGrid.getDraggedObjectData(this.draggedItem);
			}
		}
	}

	private createSnappingGridForObject(sceneObject: SceneObject): void {
		const useSimpleConnectKitsLogic = this.props.rangeSettings.simpleConnectionKitsLogic || false;

		this.clearSnappingGrid();
		this.snappingGrid.generate(sceneObject, useSimpleConnectKitsLogic);
		// create connection ghosts
		this.snappingGrid.getConnectionSnappingPoints().forEach((snappingPoint) => {
			const { transform } = snappingPoint;
			const ghost = this.objectManager.createEmptyObject(sceneObject.articleId);

			if (ghost && ghost.node) {
				if (transform && transform.position && transform.rotation) {
					ghost.transform = transform;
					ghost.node.setLocalPosition(transform.position);
					ghost.node.setRotation(transform.rotation);

					this.sceneGhosts.set(snappingPoint, ghost);

					// DEBUG
					if (this.props.showDebug) {
						const preview = ghost.node.addComponent(MeshRenderer, 'Preview');
						preview.state.mesh = { width: 1, depth: 1, height: 1 };
						preview.state.enableOverlay = true;
						preview.state.overlayColor = { r: 0, g: 1, b: 0 };
						preview.state.visibility = 0.5;
					}
				}
			} else {
				throw 'PointerManager: createSnappingGridForObject(): ghost does not exist';
			}
		});

		// create grid ghosts
		this.snappingGrid.getGridSnappingPoints().forEach((snappingPoint) => {
			const { transform, isValidLine } = snappingPoint;
			const ghost = this.objectManager.createEmptyObject(sceneObject.articleId);

			if (ghost && ghost.node) {
				if (transform && transform.position && transform.rotation) {
					ghost.transform = transform;
					ghost.node.setLocalPosition(transform.position);
					ghost.node.setRotation(transform.rotation);

					this.sceneGhosts.set(snappingPoint, ghost);

					// DEBUG
					if (this.props.showDebug) {
						const preview = ghost.node.addComponent(MeshRenderer, 'Preview');
						preview.state.mesh = isValidLine
							? { width: 0.5, depth: 0.5, height: 0.5 }
							: { width: 0.15, depth: 0.15, height: 0.15 };

						preview.state.enableOverlay = true;
						preview.state.overlayColor = isValidLine
							? { r: 0, g: 0, b: 1 }
							: { r: 0.3, g: 0.3, b: 0.3 };
						preview.state.visibility = 0.5;
					}
				}
			} else {
				throw 'SceneContainer: createSnappingGrid(): ghost does not exist';
			}
		});
	}

	private renderDebugGridBox(gridBox: GridBox, color?: Color): void {
		const gridBoxBoundings = gridBox.getBox();
		const size: BoxDimensions = {
			height: 10,
			width: Math.abs(gridBoxBoundings.right - gridBoxBoundings.left),
			depth: Math.abs(gridBoxBoundings.top - gridBoxBoundings.bottom),
		};
		const position = {
			x: (gridBoxBoundings.right + gridBoxBoundings.left) / 2,
			y: 5,
			z: (gridBoxBoundings.top + gridBoxBoundings.bottom) / 2,
		};
		const node = this.objectManager.createEmptyNode(position);
		const render = node.addComponent(MeshRenderer, 'Preview');
		render.state.mesh = size;
		render.state.visibility = 0.25;
		if (color) {
			render.state.enableOverlay = true;
			render.state.overlayColor = color;
		}
		this.debugGhosts.push(node);
	}

	private getObject(item: SceneObject | string): SceneObject {
		return (typeof item == 'string' ? this.sceneItems.get(item) : item) as SceneObject;
	}

	private saveScreenshot = (): void => {
		this.cameraController.createScreenshot((screenshot) => this.props.saveScreenshot(screenshot));
	};

	private getSpecificParent(specificItems: Map<string, SceneObject>): SceneObject {
		const specificItemsArr = Array.from(specificItems.values());
		return specificItemsArr.filter((item) => !item.articleId.includes('backrest'))[0];
	}

	public onKeyDown = (): void => {
		this.keyDown = true;
		setTimeout(() => {
			this.isPointerDown = true;
			this.onPointerUp();
		}, 100);
	};
}
